import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import configData from '../../config.json';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';

const LoyaltyBanner = ({
  restaurant,
  userProfile,
  email: emailProp,
  phone: phoneProp,
  onDiscountApplied,
}) => {
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useDataContext();

  // Prefer explicitly-supplied contact (guest checkout form) over the logged-in
  // profile, so the discount shows for guests too. Resolve to primitive strings so
  // they can be stable effect dependencies — `userProfile` is often a fresh object
  // every render (e.g. JSON.parse(localStorage...)), which would otherwise re-fire
  // the effect on every render and hammer the loyalty endpoint.
  const email = emailProp || userProfile?.profile?.Email || userProfile?.profile?.email || '';
  const phone =
    phoneProp || userProfile?.profile?.MobileNumber || userProfile?.profile?.phone || '';

  // Keep the latest onDiscountApplied callable without making it an effect
  // dependency — parents commonly pass a fresh inline callback each render.
  const onDiscountAppliedRef = useRef(onDiscountApplied);
  onDiscountAppliedRef.current = onDiscountApplied;

  useEffect(() => {
    let cancelled = false;

    // Always tell the parent the current applicable discount (including 0) so a
    // previously-applied discount is cleared when the identity changes, the lookup
    // fails, or there's no contact to check. Never leave a stale discount behind.
    const report = (pct) => {
      if (!cancelled && onDiscountAppliedRef.current) onDiscountAppliedRef.current(pct);
    };

    const run = async () => {
      setLoading(true);

      if (!restaurant?._id || (!email && !phone)) {
        if (!cancelled) {
          setLoyaltyStatus(null);
          setLoading(false);
        }
        report(0);
        return;
      }

      try {
        const params = new URLSearchParams();
        // Email-first: phone formats vary (country code) and may not match the loyalty record.
        if (email) params.append('email', email);
        else if (phone) params.append('phone', phone);

        const response = await axios.get(
          `${configData.SERVER_URL}/loyalty/status/${restaurant._id}?${params.toString()}`,
        );

        if (cancelled) return;

        const data = response?.data || null;
        const isActive = !!(data?.enabled && data?.isActive);
        const discount = isActive && Number(data?.discount) > 0 ? Number(data.discount) : 0;

        setLoyaltyStatus(data);
        report(discount);
      } catch (e) {
        // Fail-safe: any error clears the discount so we never display (or imply a
        // charge for) an unverified member benefit. The backend is authoritative.
        console.log('Loyalty check:', e.message);
        if (!cancelled) setLoyaltyStatus(null);
        report(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Debounce so rapidly-changing contact (e.g. a guest typing their email)
    // triggers at most one lookup once the input settles, not one per keystroke.
    const timer = setTimeout(run, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // onDiscountApplied is intentionally read via ref, not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?._id, email, phone]);

  // Only ever surface loyalty to ACTIVE members (the discount). Never advertise the
  // program to non-members / non-active guests — show nothing in every other case.
  if (loading || !loyaltyStatus?.enabled || !loyaltyStatus?.isActive) return null;

  // Active member only — confirm the discount that's being applied.
  return (
    <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl px-[18px] py-[14px] mb-4">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shrink-0">
          <span className="text-xl">⭐</span>
        </div>

        <div className="flex-1">
          <div className="text-sm font-bold text-green-900">
            {translations[language]?.loyalty_active_title || 'Du har medlemsrabat'}
          </div>
          <div style={{ fontSize: 12, color: '#15803d' }}>
            {loyaltyStatus.discount > 0
              ? `✓ ${loyaltyStatus.discount}% ${translations[language]?.loyalty_discount_applied || 'rabat tilføjet'}`
              : translations[language]?.loyalty_active_member || 'Aktivt medlem'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyBanner;
